/**
 * Christmas Bundle Builder
 * Handles bundle selection, variant picking, and cart integration
 */

(function() {
  'use strict';

  // Namespace for the bundle builder
  window.ChristmasBundleBuilder = window.ChristmasBundleBuilder || {};

  /**
   * Christmas Bundle Builder Class
   */
  class BundleBuilder {
    constructor(containerId) {
      this.container = document.getElementById(containerId);
      if (!this.container) {
        console.error('Christmas Bundle Builder: Container not found:', containerId);
        return;
      }

      // Load data from window object
      this.data = window.christmasBundleData && window.christmasBundleData[containerId];
      if (!this.data) {
        console.error('Christmas Bundle Builder: Data not found for:', containerId);
        return;
      }

      // State
      this.selectedBundle = null;
      this.selectedVariants = {
        protein: null,
        tshirt: null
      };
      this.quantity = 1;

      // DOM Elements
      this.bundleCards = this.container.querySelectorAll('.bundle-card');
      this.bundleContents = this.container.querySelector('.bundle-contents');
      this.bundlePurchase = this.container.querySelector('.bundle-purchase');
      this.bundleItems = this.container.querySelectorAll('.bundle-item');
      this.variantButtons = this.container.querySelectorAll('.variant-btn');
      this.addToCartBtn = this.container.querySelector('.bundle-add-to-cart');
      this.qtyInput = this.container.querySelector('.bundle-qty-input');
      this.qtyDecreaseBtn = this.container.querySelector('.bundle-qty-btn[data-action="decrease"]');
      this.qtyIncreaseBtn = this.container.querySelector('.bundle-qty-btn[data-action="increase"]');
      this.addToCartText = this.container.querySelector('.bundle-add-to-cart__text');
      this.addToCartPrice = this.container.querySelector('.bundle-add-to-cart__price');
      this.validationMessage = this.container.querySelector('.bundle-validation-message');
      this.validationMessageText = this.container.querySelector('.bundle-validation-message__text');
      this.errorMessage = this.container.querySelector('.bundle-error-message');

      // Initialize
      this.init();
    }

    init() {
      this.bindEvents();
    }

    /**
     * Bind all event listeners
     */
    bindEvents() {
      // Bundle card selection
      this.bundleCards.forEach(card => {
        card.addEventListener('click', () => this.selectBundle(card.dataset.bundle));
      });

      // Variant button selection
      this.variantButtons.forEach(btn => {
        btn.addEventListener('click', () => {
          if (!btn.disabled) {
            const selector = btn.closest('.variant-selector');
            const product = selector.dataset.product;
            this.selectVariant(product, btn);
          }
        });
      });

      // Quantity controls
      if (this.qtyDecreaseBtn) {
        this.qtyDecreaseBtn.addEventListener('click', () => this.changeQuantity(-1));
      }
      if (this.qtyIncreaseBtn) {
        this.qtyIncreaseBtn.addEventListener('click', () => this.changeQuantity(1));
      }
      if (this.qtyInput) {
        this.qtyInput.addEventListener('change', (e) => {
          const value = parseInt(e.target.value) || 1;
          this.quantity = Math.max(1, Math.min(99, value));
          this.qtyInput.value = this.quantity;
          this.updatePriceSummary();
        });
      }

      // Add to cart
      if (this.addToCartBtn) {
        this.addToCartBtn.addEventListener('click', () => this.handleAddToCart());
      }
    }

    /**
     * Select a bundle (Elf, Santa, or Ms Claus)
     */
    selectBundle(bundleType) {
      this.selectedBundle = bundleType;

      // Update card UI
      this.bundleCards.forEach(card => {
        card.classList.toggle('selected', card.dataset.bundle === bundleType);
      });

      // Show bundle contents and purchase section
      if (this.bundleContents) {
        this.bundleContents.style.display = 'block';
        this.bundleContents.classList.add('visible');
      }
      if (this.bundlePurchase) {
        this.bundlePurchase.style.display = 'block';
        this.bundlePurchase.classList.add('visible');
      }

      // Update validation message text based on bundle type
      if (this.validationMessageText) {
        const translations = this.data.translations;
        if (bundleType === 'elf') {
          this.validationMessageText.textContent = translations.elf_bundle_contents || 'Choose T-shirt size';
        } else if (bundleType === 'santa') {
          this.validationMessageText.textContent = translations.santa_bundle_contents || 'Choose T-shirt size & protein powder flavor';
        } else if (bundleType === 'ms_claus') {
          // Ms Claus has no variant selection - message not needed
          this.validationMessageText.textContent = translations.ms_claus_bundle_contents || '';
        }
      }

      // Show/hide bundle items based on selection
      this.bundleItems.forEach(item => {
        const itemBundles = item.dataset.bundles.split(',');
        const shouldShow = itemBundles.includes(bundleType);
        item.style.display = shouldShow ? 'grid' : 'none';
      });

      // Reset variant selections for hidden items
      if (bundleType === 'elf' || bundleType === 'ms_claus') {
        // Elf and Ms Claus bundles don't need protein selection
        this.selectedVariants.protein = null;
        const proteinSelector = this.container.querySelector('.variant-selector[data-product="protein"]');
        if (proteinSelector) {
          proteinSelector.querySelectorAll('.variant-btn').forEach(btn => {
            btn.classList.remove('selected');
          });
        }
      }
      
      if (bundleType === 'ms_claus') {
        // Ms Claus bundle doesn't need t-shirt selection
        this.selectedVariants.tshirt = null;
        const tshirtSelector = this.container.querySelector('.variant-selector[data-product="tshirt"]');
        if (tshirtSelector) {
          tshirtSelector.querySelectorAll('.variant-btn').forEach(btn => {
            btn.classList.remove('selected');
          });
        }
      }

      // Update price summary
      this.updatePriceSummary();

      // Validate and update button state
      this.validateSelections();
    }

    /**
     * Select a variant for a product
     */
    selectVariant(product, button) {
      const variantId = parseInt(button.dataset.variantId);
      const variantPrice = parseInt(button.dataset.variantPrice);
      const variantImage = button.dataset.variantImage;

      // Update state
      this.selectedVariants[product] = {
        id: variantId,
        price: variantPrice,
        title: button.dataset.variantTitle
      };

      // Update button UI
      const selector = button.closest('.variant-selector');
      selector.querySelectorAll('.variant-btn').forEach(btn => {
        btn.classList.remove('selected');
      });
      button.classList.add('selected');

      // Update product image if available
      if (variantImage) {
        const bundleItem = button.closest('.bundle-item');
        const img = bundleItem.querySelector('.bundle-item__main-image');
        if (img) {
          img.src = variantImage;
        }
      }

      // Update price summary
      this.updatePriceSummary();

      // Validate selections
      this.validateSelections();
    }

    /**
     * Change quantity
     */
    changeQuantity(delta) {
      const newQty = this.quantity + delta;
      if (newQty >= 1 && newQty <= 99) {
        this.quantity = newQty;
        if (this.qtyInput) {
          this.qtyInput.value = this.quantity;
        }
        this.updatePriceSummary();
      }
    }

    /**
     * Calculate total price based on bundle and variant selections
     * Uses fixed promotional bundle prices
     */
    calculateTotal() {
      if (!this.selectedBundle) return 0;

      const bundle = this.data.bundles[this.selectedBundle];
      // Use the fixed bundle discount price
      return bundle.price * this.quantity;
    }

    /**
     * Update price display in the add to cart button
     */
    updatePriceSummary() {
      if (!this.selectedBundle) return;

      const bundleData = this.data.bundles[this.selectedBundle];
      
      // Use the fixed bundle prices
      const discountPrice = bundleData.price;
      const totalDiscount = discountPrice * this.quantity;

      // Update price in button
      if (this.addToCartPrice) {
        this.addToCartPrice.textContent = this.formatMoney(totalDiscount);
      }
    }

    /**
     * Format price as money string
     */
    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      return '€' + amount;
    }

    /**
     * Validate that all required selections are made
     */
    validateSelections() {
      if (!this.selectedBundle) {
        this.setAddToCartEnabled(false);
        return false;
      }

      const bundle = this.data.bundles[this.selectedBundle];
      const translations = this.data.translations;
      let isValid = true;
      const missingSelections = [];

      // Ms Claus bundle is a single variant - no selections needed
      if (bundle.isSingleVariant) {
        this.setAddToCartEnabled(true);
        if (this.validationMessage) {
          this.validationMessage.style.display = 'none';
        }
        return true;
      }

      // Check if all variant products have selections
      bundle.items.forEach(itemKey => {
        if (itemKey === 'protein' || itemKey === 'tshirt') {
          if (!this.selectedVariants[itemKey]) {
            isValid = false;
            missingSelections.push(itemKey);
          }
        }
      });

      // Update UI
      this.setAddToCartEnabled(isValid);
      
      // Show/hide validation message and update text based on what's missing
      if (this.validationMessage) {
        this.validationMessage.style.display = isValid ? 'none' : 'block';
        
        // Update validation message text based on what's still missing
        if (!isValid && this.validationMessageText) {
          if (missingSelections.includes('tshirt') && missingSelections.includes('protein')) {
            // Both missing (Santa bundle)
            this.validationMessageText.textContent = translations.santa_bundle_contents || 'Choose T-shirt size & protein powder flavor';
          } else if (missingSelections.includes('tshirt')) {
            // Only T-shirt missing
            this.validationMessageText.textContent = translations.select_size || 'Choose T-shirt size';
          } else if (missingSelections.includes('protein')) {
            // Only protein missing
            this.validationMessageText.textContent = translations.select_flavor || 'Choose protein powder flavor';
          }
        }
      }

      return isValid;
    }

    /**
     * Enable/disable add to cart button
     */
    setAddToCartEnabled(enabled) {
      if (this.addToCartBtn) {
        this.addToCartBtn.disabled = !enabled;
      }
    }

    /**
     * Handle add to cart
     */
    async handleAddToCart() {
      if (!this.validateSelections()) {
        return;
      }

      const translations = this.data.translations;
      
      // Set loading state
      this.addToCartBtn.disabled = true;
      this.addToCartBtn.classList.add('loading');
      if (this.addToCartText) {
        this.addToCartText.textContent = translations.adding || 'Adding...';
      }

      try {
        await this.addBundleToCart();

        // Success
        if (this.addToCartText) {
          this.addToCartText.textContent = translations.added || 'Added!';
        }
        
        setTimeout(() => {
          if (this.addToCartText) {
            this.addToCartText.textContent = translations.add_to_cart || 'Add to Cart';
          }
          this.addToCartBtn.disabled = false;
          this.addToCartBtn.classList.remove('loading');
        }, 2000);

        // Trigger cart update event
        document.dispatchEvent(new CustomEvent('cart:update'));

        // Open cart drawer if available
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }

      } catch (error) {
        console.error('Failed to add bundle to cart:', error);
        this.showError(error.message || 'Failed to add to cart. Please try again.');
        if (this.addToCartText) {
          this.addToCartText.textContent = translations.add_to_cart || 'Add to Cart';
        }
        this.addToCartBtn.disabled = false;
        this.addToCartBtn.classList.remove('loading');
      }
    }

    /**
     * Add bundle items to cart
     */
    async addBundleToCart() {
      const bundle = this.data.bundles[this.selectedBundle];
      
      // Ms Claus bundle uses single variant add
      if (bundle.isSingleVariant) {
        return this.addSingleVariantToCart(bundle.variantId);
      }

      const items = [];
      const bundleProperty = this.selectedBundle === 'elf' ? 'christmas-elf' : 'christmas-santa';

      // Build items array
      bundle.items.forEach(itemKey => {
        const product = this.data.products[itemKey];
        let variantId;

        // Get variant ID
        if (itemKey === 'protein' || itemKey === 'tshirt') {
          variantId = this.selectedVariants[itemKey].id;
        } else {
          variantId = product.variantId;
        }

        if (variantId) {
          items.push({
            id: parseInt(variantId),
            quantity: this.quantity,
            properties: {
              '_bundle': bundleProperty
            }
          });
        }
      });

      if (items.length === 0) {
        throw new Error('No items to add');
      }

      // Check for UpCart
      const isUpCartActive = !!(
        window.UpCart ||
        window.upcart ||
        window.Upcart ||
        document.querySelector('script[src*="upcart"]') ||
        document.querySelector('link[href*="upcart"]')
      );

      const cart = !isUpCartActive && (
        document.querySelector('cart-notification') || 
        document.querySelector('cart-drawer')
      );

      // Build FormData
      const formData = new FormData();
      items.forEach((item, index) => {
        formData.append(`items[${index}][id]`, item.id);
        formData.append(`items[${index}][quantity]`, item.quantity);
        
        if (item.properties) {
          Object.entries(item.properties).forEach(([key, value]) => {
            formData.append(`items[${index}][properties][${key}]`, String(value));
          });
        }
      });

      // Build URL with sections for cart drawer update
      let url = '/cart/add';
      if (!isUpCartActive && cart && typeof cart.getSectionsToRender === 'function') {
        const sections = cart.getSectionsToRender().map((section) => section.id).join(',');
        const sectionsUrl = window.location.pathname;
        url += `?sections=${encodeURIComponent(sections)}&sections_url=${encodeURIComponent(sectionsUrl)}`;
      }

      // Add to cart
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
        },
        body: formData
      });

      // Retry without sections param if it fails
      if (!response.ok && response.status === 400 && url.includes('sections=')) {
        const retryResponse = await fetch('/cart/add', {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
          },
          body: formData
        });

        if (!retryResponse.ok) {
          const errorText = await retryResponse.text();
          throw new Error(errorText || 'Failed to add to cart');
        }

        return await retryResponse.json();
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to add to cart');
      }

      const data = await response.json();

      // Update cart drawer if present
      if (!isUpCartActive && cart && data.sections) {
        cart.renderContents(data);
      }

      return data;
    }

    /**
     * Add single variant to cart (for Ms Claus bundle)
     */
    async addSingleVariantToCart(variantId) {
      if (!variantId) {
        throw new Error('Ms Claus bundle variant not found');
      }

      // Check for UpCart
      const isUpCartActive = !!(
        window.UpCart ||
        window.upcart ||
        window.Upcart ||
        document.querySelector('script[src*="upcart"]') ||
        document.querySelector('link[href*="upcart"]')
      );

      const cart = !isUpCartActive && (
        document.querySelector('cart-notification') || 
        document.querySelector('cart-drawer')
      );

      // Build FormData for single item
      const formData = new FormData();
      formData.append('items[0][id]', variantId);
      formData.append('items[0][quantity]', this.quantity);
      formData.append('items[0][properties][_bundle]', 'christmas-ms-claus');

      // Build URL with sections for cart drawer update
      let url = '/cart/add';
      if (!isUpCartActive && cart && typeof cart.getSectionsToRender === 'function') {
        const sections = cart.getSectionsToRender().map((section) => section.id).join(',');
        const sectionsUrl = window.location.pathname;
        url += `?sections=${encodeURIComponent(sections)}&sections_url=${encodeURIComponent(sectionsUrl)}`;
      }

      // Add to cart
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
        },
        body: formData
      });

      // Retry without sections param if it fails
      if (!response.ok && response.status === 400 && url.includes('sections=')) {
        const retryResponse = await fetch('/cart/add', {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
          },
          body: formData
        });

        if (!retryResponse.ok) {
          const errorText = await retryResponse.text();
          throw new Error(errorText || 'Failed to add to cart');
        }

        return await retryResponse.json();
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to add to cart');
      }

      const data = await response.json();

      // Update cart drawer if present
      if (!isUpCartActive && cart && data.sections) {
        cart.renderContents(data);
      }

      return data;
    }

    /**
     * Show error message
     */
    showError(message) {
      if (this.errorMessage) {
        this.errorMessage.textContent = message;
        this.errorMessage.style.display = 'block';

        setTimeout(() => {
          this.errorMessage.style.display = 'none';
        }, 5000);
      }
    }
  }

  /**
   * Initialize a Christmas bundle builder instance
   */
  window.ChristmasBundleBuilder.init = function(containerId) {
    return new BundleBuilder(containerId);
  };

})();

